// OneDrive:CoverageThreshold(75)

import { addEventProps, EventBase, ClonedEventType, IEventProps, IEventType } from './EventBase';
import { Manager } from './Manager';
import { IEvent, IEventMetadataDeclarationTable, IEventMetadataTable } from './IEvent';
import { ResultTypeEnum } from './events/ResultTypeEnum';
import * as ErrorHelper from './ErrorHelper';
import { safeSerialize } from '@ms/utilities-objects/lib/safeSerialize';
import { IPairedEventEndData } from './IPairedEventEndData';

const schemaExceptionErrorCode = 'GetSchemaException';
const promiseCreationFailureErrorCode = 'PromiseCreationFailed';
const timeoutErrorCode = 'Timeout';

function generateQosResult(resultType: ResultTypeEnum, resultCode?: string, error?: string): IPairedEventEndData {
    const schema = <IPairedEventEndData>{
        resultType: resultType
    };
    if (resultCode) {
        schema.resultCode = resultCode;
    }
    if (error) {
        schema.error = error;
    }
    return schema;
}

export interface IPairedEvent<StartDataType, EndDataType> extends IEvent<StartDataType & EndDataType> {
    data: StartDataType & EndDataType;

    setTimeout(ms: number, data?: EndDataType): void;
    verbose(message: string): void;
    end(data?: EndDataType): void;
}

export interface IPairedEventType<StartDataType, EndDataType> extends IEventType<StartDataType & EndDataType> {
    prototype: IPairedEvent<StartDataType, EndDataType>;
    new(data?: StartDataType, parent?: IEvent): IPairedEvent<StartDataType, EndDataType>;
    instrumentPromise<
        T,
        TStart = {},
        TEnd extends IPairedEventEndData = IPairedEventEndData,
        P extends Promise<T> = Promise<T>>(
            this: { new(dataValue?: TStart, parent?: IEvent): IPairedEvent<TStart, TEnd> },
            startSchema: TStart,
            createPromise: () => P,
            getCompleteSchema?: (result?: T) => TEnd,
            getErrorSchema?: (error?: any) => TEnd, // tslint:disable-line:no-any
            timeoutMs?: number,
            timeoutSchema?: TEnd,
            parent?: IEvent): P;
}

class PairedEventBase<
    StartDataType,
    EndDataType extends IPairedEventEndData> extends EventBase<StartDataType, StartDataType & EndDataType> {
    private timeoutId: number | undefined;

    public static instrumentPromise<
        T,
        TStart = {},
        TEnd extends IPairedEventEndData = IPairedEventEndData,
        P extends Promise<T> = Promise<T>>(
            this: { new(dataValue?: TStart, parent?: IEvent): IPairedEvent<TStart, TEnd> },
            startSchema: TStart,
            createPromise: () => P,
            getCompleteSchema?: (result?: T) => TEnd,
            getErrorSchema?: (error?: any) => TEnd, // tslint:disable-line:no-any
            timeoutMs?: number,
            timeoutSchema?: TEnd,
            parent?: IEvent): P {

        let promise: P;
        const event = new this(startSchema, parent);
        if (timeoutMs) {
            event.setTimeout(timeoutMs, timeoutSchema);
        }

        const onComplete = (result?: T) => {
            let schema: IPairedEventEndData;
            if (getCompleteSchema) {
                try {
                    schema = getCompleteSchema(result);
                } catch (e) {
                    schema = generateQosResult(ResultTypeEnum.Failure, schemaExceptionErrorCode, e.toString());
                }
            } else {
                schema = generateQosResult(ResultTypeEnum.Success);
            }
            event.end(schema as TEnd);
        };

        const onError = (errorArgs?: Error) => {
            let schema: IPairedEventEndData;
            if (getErrorSchema) {
                try {
                    schema = getErrorSchema(errorArgs);
                } catch (e) {
                    schema = generateQosResult(ResultTypeEnum.Failure, schemaExceptionErrorCode, e.toString());
                }
            } else if (errorArgs) {
                const failureResultType: ResultTypeEnum = (errorArgs instanceof Error && errorArgs.name === 'Canceled') ?
                    ResultTypeEnum.ExpectedFailure : ResultTypeEnum.Failure;
                schema = generateQosResult(failureResultType, undefined, safeSerialize(errorArgs));
            } else {
                schema = generateQosResult(ResultTypeEnum.Failure);
            }
            event.end(schema as TEnd);
        };

        try {
            promise = createPromise();
        } catch (e) {
            event.end(generateQosResult(ResultTypeEnum.Failure, promiseCreationFailureErrorCode, e.toString()) as TEnd);
            throw e;
        }
        promise.then(onComplete, onError);

        return promise;
    }

    constructor(data?: StartDataType, parent?: IEvent) {
        super(data, ClonedEventType.Start, parent);
    }

    public setTimeout(ms: number, data?: EndDataType): void {
        this._clearTimeout();

        if (!data) {
            data = generateQosResult(ResultTypeEnum.Failure, timeoutErrorCode) as EndDataType;
        }
        /* tslint:disable-next-line:ban-native-functions */
        this.timeoutId = window.setTimeout(this.end.bind(this, data), ms);
    }

    public verbose(message: string): void {
        if (this.endTime) {
            // event already ended, no need to log anymore
            return;
        }

        if (this._isQosEvent()) {
            const qosSchema = this.data as { name?: string; };
            ErrorHelper.verbose(message, qosSchema.name);
        }
    }

    public end(data?: EndDataType): void {
        // Make sure end can only be called once
        if (!this.endTime) {
            if (data) {
                this._setData(data);
            }

            // Set the end time
            this.endTime = Manager.getTime();

            // Log the event end
            this._logEvent(ClonedEventType.End);

            // If this is a QOS event log and contains an error message trigger the upload of logs by calling
            // the ErrorHelper
            if (this._isQosEvent()) {
                const qosSchema: {
                    error?: Error | string,
                    name?: string,
                    resultCode?: string,
                    resultType?: ResultTypeEnum
                } = this.data;
                if (qosSchema.error) {
                    ErrorHelper.log(qosSchema.error, qosSchema.name, qosSchema.resultCode, qosSchema.resultType);
                }
            }

            this._clearTimeout();
        }
    }

    private _clearTimeout(): void {
        if (this.timeoutId) {
            /* tslint:disable-next-line:ban-native-functions */
            window.clearTimeout(this.timeoutId);
            this.timeoutId = undefined;
        }
    }

    private _isQosEvent(): boolean {
        return this.eventName.indexOf('Qos,') >= 0;
    }
}

export function createPairedEvent<StartDataType, EndDataType extends IPairedEventEndData>(
    props: IEventProps,
    metadata: IEventMetadataDeclarationTable,
    baseClass?: {
        prototype: {
            metadata: IEventMetadataTable
        }
    }): IPairedEventType<StartDataType, EndDataType> {
    class PairedEvent extends PairedEventBase<StartDataType, EndDataType> {
    }
    addEventProps(PairedEvent.prototype, props, metadata, baseClass);
    return PairedEvent as IPairedEventType<StartDataType, EndDataType>;
}
